import { AllocationEntitiesTable } from "features/allocations/AllocationEntitiesTable";
import { AllocationSetupFooter } from "features/allocations/setup/AllocationSetupFooter";
import { useAllocationSetupForm } from "features/allocations/setup/AllocationSetupForm";
import {
  useAllocationRuleActivate,
  useAllocationRuleRetrieveSuspense,
  useAllocationRuleVersionListSuspense,
} from "gen/endpoints/allocation-rule/allocation-rule";
import { useAllocationRuleEdgeCreate } from "gen/endpoints/allocation-rule-edge/allocation-rule-edge";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

export const AllocationSetupEntitiesPage = () => {
  const { fundFamilyId, allocationRuleId } = useRequiredStringParams();

  const { t } = useTranslation(["allocation"]);
  const navigate = useNavigate();

  const { data } = useAllocationRuleRetrieveSuspense(
    fundFamilyId,
    allocationRuleId,
  );

  const { form, handleSubmit, tableProps } = useAllocationSetupForm(
    fundFamilyId,
    allocationRuleId,
  );

  const { mutateAsync: createAllocationRuleEdge } =
    useAllocationRuleEdgeCreate();

  const { mutateAsync: activateAllocationRule } = useAllocationRuleActivate();

  const { data: tableData } = useAllocationRuleVersionListSuspense(
    fundFamilyId,
    allocationRuleId,
  );

  const onSubmit = useMemo(
    () =>
      handleSubmit(async (data) => {
        await createAllocationRuleEdge({
          fundFamilyId,
          data,
        });
        await activateAllocationRule({ fundFamilyId, allocationRuleId });
      }),
    [
      activateAllocationRule,
      allocationRuleId,
      createAllocationRuleEdge,
      fundFamilyId,
      handleSubmit,
    ],
  );

  return (
    <PageContent contentWidth="boxed" isFullHeight={true}>
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={() => {
          navigate("../../allocations");
        }}
      >
        <PageHeader
          title={t("allocation:setup.entities.heading", {
            name: data.name,
          })}
          workflowName={t("allocation:setup.superHeading")}
          helperText={t("allocation:setup.entities.description")}
        />
        <PageBody>
          <TableBox>
            <TableBoxHeader>
              <TableBoxTitle>{t("allocation:ruleVersions")}</TableBoxTitle>
              <DataTableBoxActions />
            </TableBoxHeader>
            <TableBoxBody>
              <DataTable data={tableData} />
            </TableBoxBody>
          </TableBox>
          <AllocationEntitiesTable {...tableProps} />
          <AllocationSetupFooter allowBack={true} />
        </PageBody>
      </Form>
    </PageContent>
  );
};
